import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEMS = [
  { vid: "V-1", item: "State pathway rows (bifurcated status, state IDR rules) confirmed per state before each launch — AZ next", loc: "Ch. 3, A-5", owner: "Counsel" },
  { vid: "V-2", item: "Surgical-assistant scope of practice, licensure/registration, title protection per state", loc: "Ch. 3, 7", owner: "Credentialing / counsel" },
  { vid: "V-3", item: "Good-faith-estimate applicability to ECH's encounter types", loc: "Ch. 3", owner: "Counsel" },
  { vid: "V-4", item: "Percentage-based RCM compensation vs. state fee-splitting restrictions, per state", loc: "Ch. 4", owner: "Counsel" },
  { vid: "V-5", item: "Worker classification memo reviewed against IRS common-law + state ABC tests (each operating state)", loc: "Ch. 8", owner: "Employment counsel" },
  { vid: "V-6", item: "Current certification requirements per certifying body (NBSTSA, ABSA, NSAA) and ECH minimums", loc: "Ch. 7", owner: "Credentialing" },
  { vid: "V-7", item: "Offshore PHI processing terms in the RCM partner's BAA chain", loc: "Ch. 6", owner: "Counsel" },
  { vid: "V-8", item: "Successor-biller contingency arrangement scope", loc: "Ch. 19", owner: "Counsel / Ops" },
  { vid: "V-9", item: "Malpractice coverage minimums per facility requirements and state norms", loc: "Ch. 7", owner: "Insurance broker" },
  { vid: "V-10", item: "Anonymous reporting channel implementation (A-3) — select and stand up", loc: "Ch. 2", owner: "Compliance Officer" },
  { vid: "V-11", item: "Full manual legal review before external representation as ECH's compliance program", loc: "All", owner: "Counsel" },
  { vid: "V-12", item: "APP hire-back arrangement: determine live vs. marketing-forward; if live anywhere, counsel this week; structure template (written agreement, two-way FMV, zero volume linkage) before SOP 10.4 becomes operative", loc: "Ch. 4, 10", owner: "Counsel — priority" },
  { vid: "V-13", item: "Federal-program screen: confirm no staffed case to date involved a Medicare / MA / Medicaid / TRICARE beneficiary; confirm the \"SelectHealth Advantage appeals\" boilerplate on the filed EOB is template text, not an MA claim", loc: "Ch. 3, 11", owner: "Counsel / Ops" },
  { vid: "V-14", item: "Corporate-practice-of-medicine doctrine per pipeline state (NY notably) — professional-entity structure requirement before entry; ERISA funding-status determination procedure for bifurcated states", loc: "Ch. 3", owner: "Counsel" },
  { vid: "V-15", item: "Per-payer claim recognition by provider type (AS modifier for PA/NP; CSA recognition varies) and provider-type composition of the July cohort and filed EOBs — realization calibration inherits the answer", loc: "Ch. 13", owner: "RCM partner / Finance" },
  { vid: "V-16", item: "Clinician compensation model: reconcile the investor-deck \"benefits funded\" economics (+$20–40K with benefits) with the per-case 1099 contractor classification — benefits imply W-2 employment; the two cannot both be true as stated. Affects V-5 and Ch. 8", loc: "Ch. 8", owner: "Employment counsel — priority" },
  { vid: "V-17", item: "Stage 9 timing: operationally Alteva enters IDR within ~30 days of the allowable; the federal IDR rules run on tighter business-day windows (open negotiation and initiation deadlines). Both can be true — the statute sets the outer legal limit, the vendor works inside it — but the manual must label which is which. Counsel to confirm the statutory citations before FINAL", loc: "Ch. 11 (12-stage exhibit), Ch. 13", owner: "Counsel" },
  { vid: "V-18", item: "Stage 11 late-payment penalty: federal law requires payment within 30 calendar days of an IDR determination and late amounts accrue interest. Management understanding is a 15% annual rate; counsel to confirm the exact rate and citation before the manual states it as law", loc: "Ch. 11 (12-stage exhibit)", owner: "Counsel" },
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "");

function ClearControls({ item, clearance, onSaved }) {
  const [open, setOpen] = useState(false);
  const [by, setBy] = useState("");
  const [note, setNote] = useState("");
  const cleared = clearance?.status === "CLEARED";

  const save = async (status) => {
    try {
      const res = await axios.put(`${API}/verify-register/${item.vid}`, {
        status,
        cleared_by: status === "CLEARED" ? by : clearance?.cleared_by || "",
        note: status === "CLEARED" ? note : clearance?.note || "",
      });
      onSaved(res.data);
      toast.success(status === "CLEARED" ? `${item.vid} marked cleared` : `${item.vid} reopened`);
      setOpen(false);
      setBy("");
      setNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  if (cleared) {
    return (
      <div className="vr-clearedbox">
        <div className="vr-clearedline" data-testid={`vr-cleared-${item.vid}`}>
          CLEARED — {clearance.cleared_by} · {fmt(clearance.updated_at)}
          {clearance.note ? <span className="vr-note"> · {clearance.note}</span> : null}
        </div>
        <button className="vr-link no-print" onClick={() => save("OPEN")} data-testid={`vr-reopen-${item.vid}`}>Reopen</button>
      </div>
    );
  }
  return (
    <div className="no-print">
      {!open ? (
        <button className="vr-link" onClick={() => setOpen(true)} data-testid={`vr-clear-${item.vid}`}>Mark cleared…</button>
      ) : (
        <div className="vr-form">
          <input className="inp" placeholder="Cleared by (counsel / authority)" value={by} onChange={(e) => setBy(e.target.value)} data-testid={`vr-by-${item.vid}`} />
          <input className="inp" placeholder="Note — what was confirmed" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`vr-note-${item.vid}`} />
          <div className="vr-btnrow">
            <button className="btn" onClick={() => save("CLEARED")} disabled={!by.trim()} data-testid={`vr-save-${item.vid}`}>Clear {item.vid}</button>
            <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VerifyRegister() {
  const [clearances, setClearances] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/verify-register`).then((r) => {
      const map = {};
      r.data.forEach((c) => { map[c.vid] = c; });
      setClearances(map);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = (c) => setClearances((p) => ({ ...p, [c.vid]: c }));
  const clearedCount = ITEMS.filter((i) => clearances[i.vid]?.status === "CLEARED").length;

  return (
    <div data-testid="verify-register">
      <div className="vr-progress">
        <span className="vr-count" data-testid="vr-progress-count">{clearedCount}/{ITEMS.length} cleared — the manual leaves draft when this reaches {ITEMS.length}</span>
        <div className="vr-bar"><div className="vr-fill" style={{ width: `${(clearedCount / ITEMS.length) * 100}%` }} /></div>
      </div>
      <table>
        <tbody>
          <tr><th style={{ width: 46 }}>#</th><th>Item</th><th style={{ width: 90 }}>Location</th><th style={{ width: 130 }}>Owner</th><th style={{ width: 250 }}>Status</th></tr>
          {ITEMS.map((it) => {
            const c = clearances[it.vid];
            const cleared = c?.status === "CLEARED";
            return (
              <tr key={it.vid}>
                <td className="q">{it.vid}</td>
                <td>{it.item}</td>
                <td>{it.loc}</td>
                <td>{it.owner}</td>
                <td>
                  <span className={`vr-pill ${cleared ? "cleared" : "open"}`} data-testid={`vr-status-${it.vid}`}>{cleared ? "CLEARED" : "OPEN"}</span>
                  <ClearControls item={it} clearance={c} onSaved={onSaved} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
